{% block sellops_order_modification_tracking_list %}
<sw-page class="sellops-order-modification-tracking-list">

    <template #smart-bar-header>
        <h2>{{ $tc('sellops-order-modification-tracking.list.title') }}</h2>
    </template>

    <template #content>
        <div style="padding: 0 20px 24px; max-width: 100%;">

            {# Summary cards row #}
            <div style="display: flex; gap: 12px; margin-bottom: 20px;">
                <div style="flex: 1; padding: 14px 16px; background: #FFF3E0; border-radius: 6px; border-left: 3px solid #FF9800;">
                    <div style="font-size: 11px; color: #E65100; text-transform: uppercase; font-weight: 600; letter-spacing: 0.5px;">
                        {{ $tc('sellops-order-modification-tracking.list.summaryPendingPayments') }}
                    </div>
                    <div style="font-size: 28px; font-weight: 700; color: #E65100; margin-top: 2px;">
                        {{ pendingCount }}
                    </div>
                </div>
                <div style="flex: 1; padding: 14px 16px; background: #FFEBEE; border-radius: 6px; border-left: 3px solid #F44336;">
                    <div style="font-size: 11px; color: #C62828; text-transform: uppercase; font-weight: 600; letter-spacing: 0.5px;">
                        {{ $tc('sellops-order-modification-tracking.list.summaryRefundsRequired') }}
                    </div>
                    <div style="font-size: 28px; font-weight: 700; color: #C62828; margin-top: 2px;">
                        {{ refundCount }}
                    </div>
                </div>
                <div style="flex: 1; padding: 14px 16px; background: #E8F5E9; border-radius: 6px; border-left: 3px solid #4CAF50;">
                    <div style="font-size: 11px; color: #2E7D32; text-transform: uppercase; font-weight: 600; letter-spacing: 0.5px;">
                        {{ $tc('sellops-order-modification-tracking.list.summaryCompleted') }}
                    </div>
                    <div style="font-size: 28px; font-weight: 700; color: #2E7D32; margin-top: 2px;">
                        {{ completedCount }}
                    </div>
                </div>
            </div>

            {# Filters row #}
            <div style="display: flex; gap: 12px; margin-bottom: 20px; align-items: flex-end;">
                <div style="flex: 1; min-width: 180px;">
                    <sw-single-select
                        :label="$tc('sellops-order-modification-tracking.list.filterPaymentStatus')"
                        :options="paymentStatusOptions"
                        v-model:value="filterPaymentStatus"
                        @update:value="onFilterChange"
                        size="small"
                    />
                </div>
                <div style="flex: 1; min-width: 180px;">
                    <sw-single-select
                        :label="$tc('sellops-order-modification-tracking.list.filterChangeType')"
                        :options="changeTypeOptions"
                        v-model:value="filterChangeType"
                        @update:value="onFilterChange"
                        size="small"
                    />
                </div>
                <div style="flex: 1; min-width: 180px;">
                    <sw-single-select
                        :label="$tc('sellops-order-modification-tracking.list.filterModificationType')"
                        :options="modificationTypeOptions"
                        v-model:value="filterModificationType"
                        @update:value="onFilterChange"
                        size="small"
                    />
                </div>
            </div>

            {# Data grid #}
            <sw-card :title="$tc('sellops-order-modification-tracking.list.gridTitle')" style="margin: 0; max-width: 100%; width: 100%;" class="sellops-tracking-grid-card">
                <template v-if="items && items.total > 0">
                    <sw-data-grid
                        :dataSource="items"
                        :columns="orderedColumns"
                        :isLoading="isLoading"
                        :showSelection="false"
                        :showActions="true"
                        :sortBy="sortBy"
                        :sortDirection="sortDirection"
                        @column-sort="onSortColumn"
                    >
                        {# Parent Order Number #}
                        <template #column-parentOrderNumber="{ item }">
                            <a @click.prevent="onOpenParentOrder(item)" style="color: #189eff; cursor: pointer; font-weight: 600;">
                                {{ item.parentOrderNumber || '–' }}
                            </a>
                        </template>

                        {# Child Order Number #}
                        <template #column-childOrderNumber="{ item }">
                            <a @click.prevent="onOpenChildOrder(item)" style="color: #189eff; cursor: pointer; font-weight: 600;">
                                {{ item.childOrderNumber || '–' }}
                            </a>
                        </template>

                        {# Modification Type #}
                        <template #column-modificationType="{ item }">
                            <sw-label
                                :variant="item.modificationType === 'replacement' ? 'info' : 'neutral'"
                                size="small"
                                appearance="pill"
                            >
                                {{ capitalize(item.modificationType) }}
                            </sw-label>
                        </template>

                        {# Change Type #}
                        <template #column-changeType="{ item }">
                            <sw-label
                                :variant="getVariantForChangeType(item.changeType)"
                                size="small"
                                appearance="pill"
                            >
                                {{ capitalize(item.changeType) }}
                            </sw-label>
                        </template>

                        {# Difference amount #}
                        <template #column-differenceAmount="{ item }">
                            <span
                                style="font-weight: 600;"
                                :style="{
                                    color: item.differenceAmount > 0 ? '#E65100' : (item.differenceAmount < 0 ? '#C62828' : '#52667a'),
                                }"
                            >
                                {{ item.differenceAmount > 0 ? '+' : '' }}{{ formatCurrency(item.differenceAmount, item.currencyIso) }}
                            </span>
                        </template>

                        {# Payment Status - editable #}
                        <template #column-paymentStatus="{ item }">
                            <sw-single-select
                                size="small"
                                :options="paymentStatusSelectOptions"
                                :value="item.paymentStatus"
                                :clearable="false"
                                @update:value="onPaymentStatusChange(item, $event)"
                                style="min-width: 150px;"
                            />
                        </template>

                        {# Parent order total #}
                        <template #column-parentOrderTotal="{ item }">
                            {{ formatCurrency(item.parentOrderTotal, item.currencyIso) }}
                        </template>

                        {# Child order total #}
                        <template #column-childOrderTotal="{ item }">
                            {{ formatCurrency(item.childOrderTotal, item.currencyIso) }}
                        </template>

                        {# Actions column #}
                        <template #actions="{ item }">
                            <sw-context-menu-item @click="onOpenParentOrder(item)">
                                {{ $tc('sellops-order-modification-tracking.list.contextViewParent') }}
                            </sw-context-menu-item>
                            <sw-context-menu-item @click="onOpenChildOrder(item)">
                                {{ $tc('sellops-order-modification-tracking.list.contextViewChild') }}
                            </sw-context-menu-item>
                        </template>
                    </sw-data-grid>
                </template>

                {# Empty state #}
                <template v-if="!isLoading && (!items || items.total === 0)">
                    <div style="text-align: center; padding: 40px 20px; color: #8a9199;">
                        <sw-icon name="regular-shopping-bag" size="50px" style="margin-bottom: 12px;" />
                        <h3 style="margin: 0 0 8px; color: #52667a;">{{ $tc('sellops-order-modification-tracking.list.emptyStateTitle') }}</h3>
                        <p style="margin: 0;">{{ $tc('sellops-order-modification-tracking.list.emptyStateMessage') }}</p>
                    </div>
                </template>
            </sw-card>

        </div>
    </template>

</sw-page>
{% endblock %}
